import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Badge,
    Text,
    Flex,
    Grid,
    Progress,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    VStack,
    HStack,
    Divider,
    Icon,
    useToast,
    Spinner,
    Tag,
    Switch,
    Input,
    Select,
    IconButton
} from '@chakra-ui/react';
import {
    FiPhone,
    FiMail,
    FiArrowLeft,
    FiMessageSquare,
    FiCheckCircle,
    FiXCircle,
    FiClock,
    FiEye,
    FiSend,
    FiTag,
    FiUsers,
    FiMessageCircle,
    FiCalendar,
    FiSmartphone,
    FiHash,
    FiPlus,
    FiX,
} from 'react-icons/fi';
import { MdHistory } from 'react-icons/md';
import { FaApple } from 'react-icons/fa';
import { BsAndroid2 } from 'react-icons/bs';

import { OverviewTab } from './ContactReportComponents/Overview';
import { ChannelBreakdownTab } from './ContactReportComponents/ChannelBreakDown';
import { TimelineTab } from './ContactReportComponents/TimeLine';
import { InteractionsTab } from './ContactReportComponents/Interactions';
import { RecentMessagesTab } from './ContactReportComponents/RecentMessages';
import Header from '../../components/Header';
import { getApiHeaders, getUserTimeZone } from '../../utils/DefaultFunction';
import API from '../../helpers/axiosInstance';
import { useNavigate, useParams } from 'react-router-dom';
import { LuBell, LuMail, LuMessageCircle, LuMessageSquare, LuPhone, LuShieldCheck, LuShieldOff, LuSmartphone, LuClock } from 'react-icons/lu';
import moment from 'moment-timezone';
import ActivityDeviceStats from './ContactReportComponents/ActivityDeviceStats';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';

/**
 * Signed-in user's name, for `updated_by` on consent changes. `username` is what the
 * rest of the app attributes actions with (see the cohort form's created_by); name and
 * email are fallbacks so the audit entry is never blank.
 */
const currentUserName = () => {
    try {
        const customer = JSON.parse(localStorage.getItem('customer')) || {};
        return customer.username || customer.name || customer.email || '';
    } catch (e) {
        return '';
    }
};

/**
 * Channel label + icon for a consent entry, exactly as specified for this timeline
 * (short labels: "Push", not "Push Notification"). Keyed by the platform channel id
 * that /consent-history returns; entry.channel_name is the fallback.
 */
/** How many consent entries are shown before "Show more". */
const CONSENT_HISTORY_PAGE = 10;

const CONSENT_CHANNEL_META = {
    1: { label: 'SMS', icon: LuMessageSquare },
    2: { label: 'WhatsApp', icon: LuMessageCircle },
    3: { label: 'Email', icon: LuMail },
    4: { label: 'Voice', icon: LuPhone },
    5: { label: 'In-App', icon: LuSmartphone },
    6: { label: 'Push', icon: LuBell },
};

/**
 * Pill-shaped chip, used instead of Badge in the consent history timeline.
 *
 * Deliberately a Box and not an HStack: Chakra's Stack clones its children to apply
 * spacing, so a raw string child is dropped — which rendered these chips as empty pills.
 * gap on an inline-flex Box gives the same spacing and keeps text children intact.
 */
const Chip = ({ children, bg = 'gray.100', color = 'gray.700', borderColor, ...rest }) => (
    <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        gap={1}
        flexShrink={0}
        bg={bg}
        color={color}
        borderWidth={borderColor ? '1px' : 0}
        borderStyle="solid"
        borderColor={borderColor}
        px={2.5}
        py={0.5}
        // rounded-md, as in the design — not a fully rounded pill
        borderRadius="md"
        fontSize="xs"
        fontWeight="600"
        lineHeight="1.7"
        whiteSpace="nowrap"
        {...rest}
    >
        {children}
    </Box>
);

const getDeviceIcon = (platform) => {
    const p = platform?.toLowerCase() || '';
    if (p.includes('ios')) return FaApple;
    if (p.includes('android') || p.includes('mt6768') || p.includes('x6525') ||
        p.includes('redmi') || p.includes('oppo') || p.includes('infinix') || p.includes('raven'))
        return BsAndroid2;
    return FiSmartphone;
};


export default function ContactReport() {
    const [isDeviceHistoryOpen, setIsDeviceHistoryOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [contactData, setContactData] = useState(null);
    const toast = useToast();
    const { id } = useParams();
    const contactId = id;
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState(0);
    const onBack = () => {
        navigate("/contacts")
    }
    const [costData, setCostData] = useState(null);

    const [channelOptIns, setChannelOptIns] = useState([]);
    const [customMetrics, setCustomMetrics] = useState([]);
    const [optInLoading, setOptInLoading] = useState({});
    const [consentHistory, setConsentHistory] = useState([]);
    const [consentHistoryLoading, setConsentHistoryLoading] = useState(true);
    // Long trails are collapsed to the most recent CONSENT_HISTORY_PAGE entries
    const [showAllConsentHistory, setShowAllConsentHistory] = useState(false);
    const [totalCost, setTotalCost] = useState({});

    // Other Contacts
    const [otherContacts, setOtherContacts] = useState([]);
    const [showAddContact, setShowAddContact] = useState(false);
    const [newContactType, setNewContactType] = useState('');
    const [newContactValue, setNewContactValue] = useState('');
    const [addingContact, setAddingContact] = useState(false);
    const [contactTypes, setContactTypes] = useState([]);
    const [loadingContactTypes, setLoadingContactTypes] = useState(false);

    // Customer Profile
    const [customerProfile, setCustomerProfile] = useState([]);
    const [showAddProfile, setShowAddProfile] = useState(false);
    const [newProfileKey, setNewProfileKey] = useState('');
    const [newProfileValue, setNewProfileValue] = useState('');
    const [addingProfile, setAddingProfile] = useState(false);

    const channelConfig = {
        1: {
            name: "SMS",
            icon: LuMessageSquare,
            color: "#155dfc",
            iconBg: "blue.100",
            iconColor: "blue.600",
            description: "Simple text messages"
        },
        2: {
            name: "Whatsapp",
            icon: LuMessageCircle,
            color: "#00a63e",
            iconBg: "green.100",
            iconColor: "green.600",
            description: "Rich messaging with media"
        },
        3: {
            name: "E-Mail",
            icon: LuMail,
            color: "#9810fa",
            iconBg: "purple.100",
            iconColor: "purple.600",
            description: "HTML emails with rich content"
        },
        4: {
            name: "Voice (IVR)",
            icon: LuPhone,
            color: "#f54a00",
            iconBg: "orange.100",
            iconColor: "orange.600",
            description: "Automated voice calls"
        },
        5: {
            name: "In-App Notification",
            icon: LuBell,
            color: "#4f39f6",
            iconBg: "indigo.100",
            iconColor: "indigo.600",
            description: "In-app messages and alerts"
        },
        6: {
            name: "Push Notification",
            icon: LuBell,
            color: "#e60076",
            iconBg: "pink.100",
            iconColor: "pink.600",
            description: "Mobile app notifications"
        },
        7: {
            name: "SMS Sec",
            icon: LuMessageSquare,
            color: "#0088cc",
            iconBg: "cyan.100",
            iconColor: "cyan.600",
            description: "Secondary SMS channel"
        }
    };

    const fetchContactStats = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/stats`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setContactData(response.data.data);
                setChannelOptIns(response.data.data.channel_opt_in);
                setCostData(response.data.data.total_cost_of_messages);
                setCustomMetrics(response.data.data.custom_metrics || []);
                setTotalCost(response.data.data.total_cost_of_messages || {});
                setOtherContacts((response.data.data.contact?.other_contact || []).map(c => ({ contact_type_id: c.contact_type_id ?? c.name, value: c.value, _id: c._id })));
                setCustomerProfile((response.data.data.contact?.customer_profile || []).map(p => ({ key: p.name, value: p.value, _id: p._id })));

            }
        } catch (error) {
            console.error("Error fetching contact stats:", error);
            toast({
                title: "Error",
                description: "Failed to fetch contact statistics",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };
    /**
     * Consent decisions captured for this contact, newest first. Sibling of the
     * channel-opt-in endpoint the toggles above use: opt-in is the CURRENT state,
     * this is the audit trail of how it got there.
     */
    const fetchConsentHistory = async ({ silent = false } = {}) => {
        // A refresh triggered by a toggle keeps the existing timeline on screen —
        // swapping it for a spinner every time a switch is flipped would flicker.
        if (!silent) setConsentHistoryLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/consent-history`;
            const response = await API.get(url, { headers: getApiHeaders() });
            const list = response?.data?.data?.consent_history || response?.data?.consent_history || [];
            // Sort defensively: the timeline reads top-down as most recent first.
            setConsentHistory(
                [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            );
        } catch (error) {
            console.error("Error fetching consent history:", error);
            // Only clear on the initial load; a failed background refresh should leave
            // the entries already on screen alone rather than blanking them.
            if (!silent) setConsentHistory([]);
        } finally {
            if (!silent) setConsentHistoryLoading(false);
        }
    };

    // Fetch contact stats from API
    useEffect(() => {
        if (contactId) {
            fetchContactStats();
            fetchConsentHistory();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId]);

    useEffect(() => {
        const fetchContactTypes = async () => {
            setLoadingContactTypes(true);
            try {
                const res = await API.get(`${process.env.REACT_APP_API_URL}/contacts/other-contact-types`, { headers: getApiHeaders() });
                const raw = res.data?.data;
                setContactTypes(Array.isArray(raw) ? raw : (raw?.types || []));
            } catch {
                setContactTypes([]);
            } finally {
                setLoadingContactTypes(false);
            }
        };
        fetchContactTypes();
    }, []);

    // Map API response to component data
    const mapContactData = () => {
        if (!contactData) return null;
        const rawDeviceInfo = contactData.subscriber?.device_info;

        let parsedDeviceInfo = null;

        try {
            parsedDeviceInfo =
                typeof rawDeviceInfo === 'string'
                    ? JSON.parse(rawDeviceInfo)
                    : rawDeviceInfo;
        } catch (e) {
            parsedDeviceInfo = null;
        }

        const contact = {
            customerName: contactData.contact?.name || 'N/A',
            number: contactData.contact?.phone,
            email: contactData.contact?.email || 'N/A',
            // Raw address, without the 'N/A' display fallback above. Used as the
            // message-log search key when a contact has no phone — passing the
            // display value there would search for the literal string "N/A".
            emailAddress: contactData.contact?.email || '',

            tags: contactData.contact?.tags?.map(tag => tag.name) || [],
            cohorts: contactData.contact?.cohorts?.map(cohort => cohort.cohort_name) || [],
            createdAt: contactData.contact?.created_at,


            whatsappAvailable: contactData.whatsapp_status === 'Available',
            firstWhatsappDeliveryDate: contactData.first_whatsapp_delivered,
            firstMessageSentDate: contactData.first_message_sent,

            subscriberId: contactData.subscriber?.subscriber_id,

            // ✅ keep original stringified device info
            deviceInfoRaw: rawDeviceInfo,

            // ✅ parsed device info object
            deviceInfo: parsedDeviceInfo,

            deviceType: parsedDeviceInfo?.deviceBrand || 'Mobile',

            deviceOS: parsedDeviceInfo?.deviceBrand === "Apple" ? `IOS ${parsedDeviceInfo?.osVersion || ''}`.trim() : `Android ${parsedDeviceInfo?.osVersion || ''}`.trim(),

            deviceModel: `${parsedDeviceInfo?.deviceBrand || ''} ${parsedDeviceInfo?.deviceModel || ''}`.trim(),
            activeDevice: contactData.device_history?.devices?.length > 0 ? contactData.device_history?.devices[0]?.active : false,
            deviceHistory: contactData.device_history?.devices?.map(device => ({
                subscriberId: device.subscriber_id,
                deviceType: device?.deviceType,
                deviceOS: device.platform,
                deviceModel: device.device_model,
                timezone: device.timezone,
                activatedDate: device.created_at,
                deactivatedDate: device.updated_at,
                status: device.active ? 'active' : 'inactive',
                activeDevice: device?.active
            })) || [],
            // updated_at:device.updated_at
        };


        const messageStats = {
            totalMessages: contactData.totals?.total || 0,
            delivered: contactData.totals?.delivered || 0,
            failed: contactData.totals?.failed || 0,
            pending: contactData.totals?.pending || 0,
            read: contactData.totals?.read || 0,
            deliveryRate: contactData.totals?.delivery_rate_percent || 0,
            readRate: contactData.totals?.read_rate_percent || 0,
            avgDeliveryTime: contactData.totals?.avg_delivery_time_secs || 0
        };





        return { contact, messageStats };
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };






    const calculateDuration = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const duration = end.getTime() - start.getTime();
        const days = Math.floor(duration / (1000 * 60 * 60 * 24));
        const hours = Math.floor((duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
        return `${days}d ${hours}h ${minutes}m`;
    };

    if (loading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Flex justify="center" align="center" minH="400px">
                        <Spinner size="xl" color="blue.500" />
                    </Flex>
                </BoxWrapper>
            </>
        );
    }

    if (!contactData) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Flex justify="center" align="center" minH="400px">
                        <Text>No contact data found</Text>
                    </Flex>
                </BoxWrapper>
            </>
        );
    }

    const handleAddContact = async () => {
        if (!newContactType || !newContactValue) return;
        setAddingContact(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/other-contacts`;
            const response = await API.post(url, {
                contact_type_id: newContactType,
                value: newContactValue,
                contact_id: contactId,
            }, { headers: getApiHeaders() });

            if (response.data.success) {
                const newEntry = response.data.data?.other_contact?.slice(-1)[0];
                setOtherContacts(prev => [...prev, {
                    contact_type_id: newContactType,
                    value: newContactValue,
                    _id: newEntry?._id
                }]);
                setNewContactType('');
                setNewContactValue('');
                setShowAddContact(false);
                toast({ title: 'Contact added', status: 'success', duration: 2000, isClosable: true });
            } else {
                toast({ title: 'Error', description: response.data.message || 'Failed to add contact', status: 'error', duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed to add contact', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setAddingContact(false);
        }
    };

    const handleRemoveContact = async (contactId) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/contact/delete-other-contact`;
            const response = await API.delete(url, {
                headers: getApiHeaders(),
                data: { contact_id: id, _id: contactId },
            });
            if (response.data.success) {
                setOtherContacts(prev => prev.filter(c => c._id !== contactId));
                toast({ title: 'Contact removed', status: 'success', duration: 2000, isClosable: true });
            } else {
                toast({ title: 'Error', description: response.data.message || 'Failed to remove contact', status: 'error', duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed to remove contact', status: 'error', duration: 3000, isClosable: true });
        }
    };

    const handleAddProfile = async () => {
        if (!newProfileKey || !newProfileValue) return;
        setAddingProfile(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/customer-profile`;
            const response = await API.post(url, {
                name: newProfileKey,
                value: newProfileValue,
                contact_id: contactId,
            }, { headers: getApiHeaders() });

            if (response.data.success) {
                const newEntry = response.data.data?.customer_profile?.slice(-1)[0];
                setCustomerProfile(prev => [...prev, {
                    key: newProfileKey,
                    value: newProfileValue,
                    _id: newEntry?._id
                }]);
                setNewProfileKey('');
                setNewProfileValue('');
                setShowAddProfile(false);
                toast({ title: 'Profile detail added', status: 'success', duration: 2000, isClosable: true });
            } else {
                toast({ title: 'Error', description: response.data.message || 'Failed to add profile detail', status: 'error', duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed to add profile detail', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setAddingProfile(false);
        }
    };

    const handleRemoveProfile = async (profileId) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/contact/delete-customer-profile`;
            const response = await API.delete(url, {
                headers: getApiHeaders(),
                data: { contact_id: id, _id: profileId },
            });
            if (response.data.success) {
                setCustomerProfile(prev => prev.filter(p => p._id !== profileId));
                toast({ title: 'Profile detail removed', status: 'success', duration: 2000, isClosable: true });
            } else {
                toast({ title: 'Error', description: response.data.message || 'Failed to remove profile detail', status: 'error', duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed to remove profile detail', status: 'error', duration: 3000, isClosable: true });
        }
    };

    const mappedData = mapContactData();
    const { contact, messageStats } = mappedData;
    const handleOptInToggle = async (channelId, currentStatus) => {
        const newStatus = !currentStatus;

        // Set loading state for this specific channel
        setOptInLoading(prev => ({ ...prev, [channelId]: true }));

        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/channel-opt-in`;
            const response = await API.patch(url, {
                channel_id: channelId,
                enabled: newStatus,
                // attribution for the consent audit trail: this decision came from the
                // portal, and from whoever is signed in
                source: 'Portal',
                updated_by: currentUserName(),
            }, { headers: getApiHeaders() });

            if (response.data.success) {
                // Update local state
                setChannelOptIns(prev =>
                    prev.map(ch =>
                        ch.channel_id === channelId
                            ? { ...ch, enabled: newStatus, updated_at: new Date().toISOString() }
                            : ch
                    )
                );

                // The toggle just wrote a new consent decision, so the audit trail below
                // is now stale — pull it again (not awaited: the switch has already
                // flipped, the timeline can fill in a moment later).
                fetchConsentHistory({ silent: true });

                toast({
                    title: "Success",
                    description: `${channelConfig[channelId]?.name} ${newStatus ? 'enabled' : 'disabled'} successfully`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error updating channel preference:', error);
            toast({
                title: "Error",
                description: "Failed to update channel preference",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setOptInLoading(prev => ({ ...prev, [channelId]: false }));
        }
    };
    return (
        <>
            <Header />
            <BoxWrapper>
                <VStack spacing={4} align="stretch" p={1}>

                    {/* Back + Title */}
                    <VStack align="flex-start" spacing={2}>
                        <Button
                            leftIcon={<FiArrowLeft />}
                            _hover={{ bg: "#0000001a !important", color: "black !important" }}
                            onClick={onBack}
                            size="sm"
                            variant="outline"
                        >
                            Back to Contacts
                        </Button>
                        <Box>
                            <Text fontSize="xl" fontWeight="600">Contact Report</Text>
                            <Text color="gray.600">Detailed analytics and message history for {contact.number}</Text>
                        </Box>
                    </VStack>

                    {/* ── Two-column layout ── */}
                    <Grid templateColumns={{ base: '1fr', lg: '400px 1fr' }} gap={5} alignItems="flex-start">

                        {/* ════ LEFT SIDEBAR ════ */}
                        <VStack spacing={4} align="stretch">

                            {/* Contact Information Card */}
                            <Box bg={"rgb(var(--brand-primary-rgb) / 0.102)"} border={"1px solid"} borderRadius={"7.85px"} borderColor="rgb(var(--brand-primary-rgb) / 0.2)" p={4}>
                                <Text fontSize={"sm"} fontWeight={"600"} mb={3}>Contact Information</Text>
                                <VStack align="stretch" spacing={4}>

                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Customer Name</Text>
                                        <Text fontSize="sm" fontWeight="medium">{contact.customerName}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Phone Number</Text>
                                        <HStack>
                                            <Icon as={FiPhone} color="gray.500" boxSize={3.5} />
                                            <Text fontSize="sm" fontWeight="medium">{contact.number}</Text>
                                        </HStack>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Email Address</Text>
                                        <HStack>
                                            <Icon as={FiMail} color="gray.500" boxSize={3.5} />
                                            <Text fontSize="sm" fontWeight="medium">{contact.email}</Text>
                                        </HStack>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Account Created</Text>
                                        <HStack>
                                            <Icon as={FiCalendar} color="gray.500" boxSize={3.5} />
                                            <Text fontSize="sm" fontWeight="medium">{formatDate(contact.createdAt)}</Text>
                                        </HStack>
                                    </Box>

                                    <Divider />

                                    {/* Other Contacts */}
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="500">Other Contacts</Text>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                bg={"white"}
                                                leftIcon={<Icon as={FiPlus} boxSize={3} />}
                                                onClick={() => setShowAddContact(!showAddContact)}
                                            >
                                                Add
                                            </Button>
                                        </Flex>

                                        {showAddContact && (
                                            <VStack spacing={2} mb={3} p={3} borderRadius="md" border="1px solid" borderColor="gray.200" bg="white">
                                                <Select
                                                    size="sm"
                                                    placeholder={loadingContactTypes ? 'Loading...' : 'Select type'}
                                                    value={newContactType}
                                                    onChange={(e) => setNewContactType(e.target.value)}
                                                    disabled={loadingContactTypes}
                                                >
                                                    {contactTypes.map(ct => (
                                                        <option key={ct.id} value={ct.id}>{ct.name}</option>
                                                    ))}
                                                </Select>
                                                <Input
                                                    size="sm"
                                                    placeholder="Enter value"
                                                    value={newContactValue}
                                                    onChange={(e) => setNewContactValue(e.target.value)}
                                                />
                                                <HStack w="full">
                                                    <Button size="xs" bg="var(--brand-primary)" color={"white"} flex={1} onClick={handleAddContact} isLoading={addingContact}>Add</Button>
                                                    <Button size="xs" variant="outline" flex={1} onClick={() => { setShowAddContact(false); setNewContactType(''); setNewContactValue(''); }} isDisabled={addingContact}>Cancel</Button>
                                                </HStack>
                                            </VStack>
                                        )}

                                        {otherContacts.length > 0 ? (
                                            <VStack align="stretch" spacing={2}>
                                                {otherContacts.map((c, i) => (
                                                    <Flex key={i} justify="space-between" align="center">
                                                        <Box>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {contactTypes.find(ct => String(ct.id) === String(c.contact_type_id))?.name || c.contact_type_id}
                                                            </Text>
                                                            <Text fontSize="sm" fontWeight="medium">{c.value}</Text>
                                                        </Box>
                                                        <IconButton
                                                            icon={<Icon as={FiX} />}
                                                            size="xs"
                                                            variant="ghost"
                                                            aria-label="Remove"
                                                            onClick={() => handleRemoveContact(c?._id)}
                                                        />
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        ) : (
                                            <Text fontSize="xs" color="gray.400">No other contacts added</Text>
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* Customer Profile */}
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="500">Customer Profile</Text>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                bg={"white"}
                                                leftIcon={<Icon as={FiPlus} boxSize={3} />}
                                                onClick={() => setShowAddProfile(!showAddProfile)}
                                            >
                                                Add Detail
                                            </Button>
                                        </Flex>

                                        {showAddProfile && (
                                            <VStack spacing={2} mb={3} p={3} borderRadius="md" border="1px solid" borderColor="gray.200" bg="white">
                                                <Input
                                                    size="sm"
                                                    placeholder="Key (e.g. Company Name)"
                                                    value={newProfileKey}
                                                    onChange={(e) => setNewProfileKey(e.target.value)}
                                                />
                                                <Input
                                                    size="sm"
                                                    placeholder="Value"
                                                    value={newProfileValue}
                                                    onChange={(e) => setNewProfileValue(e.target.value)}
                                                />
                                                <HStack w="full">
                                                    <Button size="xs" bg="var(--brand-primary)" color={"white"} flex={1} onClick={handleAddProfile} isLoading={addingProfile}>Add</Button>
                                                    <Button size="xs" variant="outline" flex={1} onClick={() => { setShowAddProfile(false); setNewProfileKey(''); setNewProfileValue(''); }} isDisabled={addingProfile}>Cancel</Button>
                                                </HStack>
                                            </VStack>
                                        )}

                                        {customerProfile.length > 0 ? (
                                            <VStack align="stretch" spacing={2}>
                                                {customerProfile.map((p, i) => (
                                                    <Flex key={i} justify="space-between" align="center">
                                                        <Box>
                                                            <Text fontSize="xs" color="gray.500">{p.key}</Text>
                                                            <Text fontSize="sm" fontWeight="medium">{p.value}</Text>
                                                        </Box>
                                                        <IconButton
                                                            icon={<Icon as={FiX} />}
                                                            size="xs"
                                                            variant="ghost"
                                                            aria-label="Remove"
                                                            onClick={() => handleRemoveProfile(p?._id)}
                                                        />
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        ) : (
                                            <Text fontSize="xs" color="gray.400">No profile details added</Text>
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* WhatsApp Status */}
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={2}>WhatsApp Status</Text>
                                        {contact.whatsappAvailable ? (
                                            <VStack align="start" spacing={1}>
                                                <Tag border={"1px solid #b9f8cf"} color={"#008236"} bg={"#f0fdf4"} size="sm">
                                                    <HStack spacing={1}>
                                                        <Icon as={FiMessageCircle} boxSize={3} />
                                                        <Text>Available</Text>
                                                    </HStack>
                                                </Tag>
                                                {contact.firstWhatsappDeliveryDate && (
                                                    <Text fontSize="xs" color="gray.500">
                                                        First delivered: {formatDate(contact.firstWhatsappDeliveryDate)}
                                                    </Text>
                                                )}
                                            </VStack>
                                        ) : (
                                            <Tag colorScheme="gray" size="sm">
                                                <HStack spacing={1}>
                                                    <Icon as={FiMessageCircle} boxSize={3} />
                                                    <Text>Not Available</Text>
                                                </HStack>
                                            </Tag>
                                        )}
                                    </Box>

                                    {/* Tags */}
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={2}>Tags</Text>
                                        <Flex flexWrap="wrap" gap={1}>
                                            {contact.tags.length > 0 ? (
                                                contact.tags.map((tag, index) => (
                                                    <Tag key={index} border={"1px solid rgb(var(--brand-primary-rgb) / 0.302)"} bg={"transparent"} size={"sm"}>
                                                        <HStack spacing={1}>
                                                            <Icon as={FiTag} boxSize={3} />
                                                            <Text>{tag}</Text>
                                                        </HStack>
                                                    </Tag>
                                                ))
                                            ) : (
                                                <Text fontSize="xs" color="gray.500">No tags</Text>
                                            )}
                                        </Flex>
                                    </Box>

                                    {/* Cohorts */}
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={2}>Cohorts</Text>
                                        <Flex flexWrap="wrap" gap={1}>
                                            {contact.cohorts.length > 0 ? (
                                                contact.cohorts.map((cohort, index) => (
                                                    <Badge key={index} colorScheme="purple" fontSize="xs">
                                                        <HStack spacing={1}>
                                                            <Icon as={FiUsers} boxSize={3} />
                                                            <Text>{cohort}</Text>
                                                        </HStack>
                                                    </Badge>
                                                ))
                                            ) : (
                                                <Text fontSize="xs" color="gray.500">No cohorts</Text>
                                            )}
                                        </Flex>
                                    </Box>
                                </VStack>
                            </Box>

                            {/* Channel Opt-In Card */}
                            <Box className='box-style-card' p={4}>
                                <Text fontSize="sm" fontWeight="600" mb={1}>Channel Opt-In</Text>
                                <Text fontSize="xs" color="gray.500" mb={3}>Manage channel preferences</Text>
                                <VStack spacing={2} align="stretch">
                                    {channelOptIns.map((channel) => {
                                        const config = channelConfig[channel.channel_id];
                                        if (!config) return null;
                                        return (
                                            <Box
                                                key={channel.channel_id}
                                                p={2}
                                                borderRadius="lg"
                                                borderWidth="1px"
                                                borderColor="gray.200"
                                                bg="white"
                                            >
                                                <HStack justify="space-between" align="center">
                                                    <HStack spacing={2} flex={1}>
                                                        <Box bg={config.iconBg} borderRadius="md" p={1.5} display="flex" alignItems="center" justifyContent="center">
                                                            <Icon as={config.icon} boxSize={4} color={config.iconColor} />
                                                        </Box>
                                                        <VStack align="start" spacing={0} flex={1}>
                                                            <Text fontSize="xs" fontWeight="500">{config.name}</Text>
                                                            <Text fontSize="xs" color="gray.400">{config.description}</Text>
                                                        </VStack>
                                                    </HStack>
                                                    <Switch
                                                        size="sm"
                                                        sx={{
                                                            '& .chakra-switch__track': { bg: 'gray.300' },
                                                            '& .chakra-switch__track[data-checked]': { bg: 'var(--brand-primary)' },
                                                        }}
                                                        isChecked={channel.enabled}
                                                        onChange={() => handleOptInToggle(channel.channel_id, channel.enabled)}
                                                        isDisabled={optInLoading[channel.channel_id]}
                                                    />
                                                </HStack>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </Box>

                            {/* Consent Capturing History — the audit trail behind the
                                Channel Opt-In toggles above. */}
                            <Box className='box-style-card' p={4}>
                                <Flex justify="space-between" align="start" gap={3} mb={3}>
                                    <Box>
                                        <HStack spacing={2}>
                                            <Icon as={LuShieldCheck} boxSize={4} color="purple.600" />
                                            <Text fontSize="md" fontWeight="600">Consent Capturing History</Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.500" mt={1}>
                                            A log of consent decisions captured for each channel. Each entry records
                                            the channel, timestamp, and whether consent was given or declined.
                                        </Text>
                                    </Box>
                                    {consentHistory.length > 0 && (
                                        <Chip bg="purple.50" color="purple.700" borderColor="purple.300">
                                            {consentHistory.length} {consentHistory.length > 1 ? "Entries" : "Entry"}
                                        </Chip>
                                    )}
                                </Flex>

                                {consentHistoryLoading ? (
                                    <HStack spacing={2} py={4} justify="center" color="gray.500">
                                        <Spinner size="sm" />
                                        <Text fontSize="xs">Loading consent history…</Text>
                                    </HStack>
                                ) : consentHistory.length === 0 ? (
                                    /* Empty state, not a bare line of grey text: a dashed
                                       placeholder reads as "this panel is ready and waiting"
                                       rather than as something that failed to load. */
                                    <VStack
                                        spacing={3}
                                        py={8}
                                        px={4}
                                        borderWidth="1px"
                                        borderStyle="dashed"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="gray.50"
                                    >
                                        <Flex
                                            align="center"
                                            justify="center"
                                            boxSize="44px"
                                            borderRadius="full"
                                            bg="purple.50"
                                            borderWidth="1px"
                                            borderColor="purple.100"
                                        >
                                            <Icon as={LuShieldCheck} boxSize={5} color="purple.400" />
                                        </Flex>
                                        <VStack spacing={1}>
                                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                                                No consent history yet
                                            </Text>
                                            <Text
                                                fontSize="xs"
                                                color="gray.500"
                                                textAlign="center"
                                                maxW="320px"
                                                lineHeight="1.6"
                                            >
                                                Consent decisions appear here as soon as a channel is
                                                opted in or out — from the toggles above or any other
                                                capture source.
                                            </Text>
                                        </VStack>
                                    </VStack>
                                ) : (
                                    <Box>
                                      {/* The spine is positioned against the list alone, so it stops at
                                          the last visible dot instead of running down behind the
                                          "Show more" button. */}
                                      <Box position="relative">
                                        {/* Timeline spine, stopped short of the first and last dots */}
                                        <Box
                                            position="absolute"
                                            left="19px"
                                            top="12px"
                                            bottom="12px"
                                            w="1px"
                                            bg="gray.200"
                                        />
                                        <VStack align="stretch" spacing={0}>
                                            {(showAllConsentHistory
                                                ? consentHistory
                                                : consentHistory.slice(0, CONSENT_HISTORY_PAGE)
                                            ).map((entry) => {
                                                const isGiven = entry.consent === true || entry.consent === 1;
                                                const meta = CONSENT_CHANNEL_META[entry.channel_id];
                                                const ChannelIcon = meta?.icon || LuMessageSquare;
                                                const channelName = meta?.label || entry.channel_name || `Channel ${entry.channel_id}`;
                                                const at = entry.created_at
                                                    ? moment.utc(entry.created_at).tz(getUserTimeZone())
                                                    : null;
                                                return (
                                                    <Flex key={entry._id} align="start" gap={3} py={3} position="relative">
                                                        {/* Timeline dot */}
                                                        <Flex
                                                            position="relative"
                                                            zIndex={1}
                                                            flexShrink={0}
                                                            w="40px"
                                                            h="40px"
                                                            borderRadius="full"
                                                            align="center"
                                                            justify="center"
                                                            borderWidth="2px"
                                                            bg={isGiven ? 'green.50' : 'red.50'}
                                                            borderColor={isGiven ? 'green.300' : 'red.300'}
                                                            color={isGiven ? 'green.700' : 'red.600'}
                                                        >
                                                            <Icon as={isGiven ? LuShieldCheck : LuShieldOff} boxSize={4} />
                                                        </Flex>

                                                        {/* Entry */}
                                                        <Box
                                                            flex={1}
                                                            minW={0}
                                                            bg="gray.50"
                                                            borderWidth="1px"
                                                            borderColor="gray.200"
                                                            borderRadius="lg"
                                                            px={3}
                                                            py={2.5}
                                                        >
                                                            <Flex align="center" justify="space-between" gap={2} flexWrap="wrap">
                                                                <HStack spacing={1.5} minW={0}>
                                                                    <Icon as={ChannelIcon} boxSize={3.5} color="gray.500" />
                                                                    <Text fontSize="sm" fontWeight="600" color="gray.500" noOfLines={1}>
                                                                        {channelName}
                                                                    </Text>
                                                                </HStack>
                                                                <Chip
                                                                    px={2}
                                                                    bg={isGiven ? 'green.100' : 'red.100'}
                                                                    color={isGiven ? 'green.800' : 'red.700'}
                                                                    borderColor={isGiven ? 'green.300' : 'red.300'}
                                                                >
                                                                    {isGiven ? '✓ Consent Given' : '✗ No Consent'}
                                                                </Chip>
                                                            </Flex>
                                                            <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
                                                                <Icon as={LuClock} boxSize={3} color="gray.400" />
                                                                <Text fontSize="xs" color="gray.500">
                                                                    {at ? at.format('DD MMM YY · hh:mm:ss A') : '—'}
                                                                </Text>
                                                            </HStack>
                                                            {/* Source and who made the change share the line below the
                                                                timestamp: a user name has no length limit, so keeping
                                                                both off the timestamp row means a long one wraps here
                                                                instead of squeezing the date. */}
                                                            {(entry.source || entry.updated_by) && (
                                                                <HStack
                                                                    spacing={1.5}
                                                                    mt={0.5}
                                                                    flexWrap="wrap"
                                                                    align="baseline"
                                                                >
                                                                    {/* label stays light; the value is what you scan for,
                                                                        so it is bold and near-black */}
                                                                    {entry.source && (
                                                                        <Text fontSize="xs" color="gray.400">
                                                                            Source:{' '}
                                                                            <Text as="span" fontWeight="600" color="gray.800">
                                                                                {entry.source}
                                                                            </Text>
                                                                        </Text>
                                                                    )}
                                                                    {entry.source && entry.updated_by && (
                                                                        <Text fontSize="xs" color="gray.300">·</Text>
                                                                    )}
                                                                    {entry.updated_by && (
                                                                        <Text
                                                                            fontSize="xs"
                                                                            color="gray.400"
                                                                            wordBreak="break-word"
                                                                        >
                                                                            Updated by:{' '}
                                                                            <Text as="span" fontWeight="600" color="gray.800">
                                                                                {entry.updated_by}
                                                                            </Text>
                                                                        </Text>
                                                                    )}
                                                                </HStack>
                                                            )}
                                                            {entry.note && (
                                                                <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">
                                                                    {entry.note}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    </Flex>
                                                );
                                            })}
                                        </VStack>
                                      </Box>

                                        {consentHistory.length > CONSENT_HISTORY_PAGE && (
                                            <Button
                                                size="xs"
                                                variant="link"
                                                color="var(--brand-primary)"
                                                mt={2}
                                                ml="52px"
                                                alignSelf="flex-start"
                                                /* index.css has a global `.chakra-button:hover` that forces a
                                                   purple fill and white text with !important, which turns a
                                                   link-style button into a filled pill on hover. Chakra's
                                                   _hover cannot outrank !important, so override it here. */
                                                sx={{
                                                    background: 'transparent !important',
                                                    '&:hover': {
                                                        background: 'transparent !important',
                                                        color: 'var(--brand-primary-active) !important',
                                                        textDecoration: 'underline',
                                                    },
                                                    '&:active, &:focus': {
                                                        background: 'transparent !important',
                                                        boxShadow: 'none',
                                                    },
                                                }}
                                                onClick={() => setShowAllConsentHistory((v) => !v)}
                                            >
                                                {showAllConsentHistory
                                                    ? 'Show less'
                                                    : `Show ${consentHistory.length - CONSENT_HISTORY_PAGE} more`}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Device Information Card */}
                            <Box className='box-style-card'>
                                <Box pb={2}>
                                    <Flex justify="space-between" align="center">
                                        <Text fontSize={"sm"} fontWeight={"500"}>Device Information</Text>
                                        <Icon as={FiSmartphone} color="gray.500" />
                                    </Flex>
                                </Box>
                                <Box>
                                    {(contact.deviceType && contact.deviceOS && contact?.deviceHistory?.length > 0) ? (
                                        <VStack align="stretch" spacing={3}>
                                            <Box bgGradient="linear(to-br, blue.50, purple.50)" borderRadius="lg" p={2} borderWidth="1px" borderColor="blue.100">
                                                <Flex justify="space-between" alignItems="start">
                                                    <HStack align="start">
                                                        <Box bg="white" borderRadius="lg" p={2} shadow="lg">
                                                            <Icon as={getDeviceIcon(contact.deviceOS)} boxSize={6} color="#155dfc" />
                                                        </Box>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontSize="lg" fontWeight="semibold">{contact.deviceType}</Text>
                                                            {contact.deviceOS && (
                                                                <Text fontSize="sm" color="gray.600">{contact.deviceOS}</Text>
                                                            )}
                                                            {contact.deviceModel && (
                                                                <Text fontSize="xs" color="gray.500">{contact.deviceModel}</Text>
                                                            )}
                                                            {contact.subscriberId && (
                                                                <VStack align="flex-start" spacing={0} mt={2}>
                                                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Subscriber ID</Text>
                                                                    <Text fontSize="xs" color="gray.600" fontFamily="monospace" wordBreak="break-all" lineHeight="1.4">
                                                                        {contact.subscriberId}
                                                                    </Text>
                                                                </VStack>
                                                            )}
                                                        </VStack>
                                                    </HStack>
                                                    {contact.activeDevice
                                                        ? <Tag color={"white"} bg={"#00c951"} fontSize={"xs"} mx={0}>Active</Tag>
                                                        : <Tag color={"white"} bg={"red"} fontSize={"xs"} mx={0}>Inactive</Tag>
                                                    }
                                                </Flex>
                                            </Box>
                                            {contact.deviceHistory && contact.deviceHistory.length > 0 && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    leftIcon={<MdHistory />}
                                                    onClick={() => setIsDeviceHistoryOpen(true)}
                                                    rightIcon={contact.deviceHistory.length}
                                                >
                                                    View Device History
                                                </Button>
                                            )}
                                        </VStack>
                                    ) : (
                                        <Text fontSize="sm" color="gray.600">No device data</Text>
                                    )}
                                </Box>
                            </Box>
                        </VStack>

                        {/* ════ RIGHT COLUMN ════ */}
                        <VStack spacing={4} align="stretch" minW="0">

                            {/* Message Statistics */}
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Total Messages</Text>
                                            <Icon as={FiMessageSquare} color="gray.500" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold">{messageStats.totalMessages.toLocaleString()}</Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>All time</Text>
                                    </Box>
                                </Box>

                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Delivered</Text>
                                            <Icon as={FiSend} color="#00a63e" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold" color="#00a63e">{messageStats.delivered.toLocaleString()}</Text>
                                        <Text fontSize="xs" color="gray.600" mt={2}>{messageStats.deliveryRate.toFixed(1)}% delivery rate</Text>
                                        <Progress value={messageStats.deliveryRate} size={"sm"} mt={2} borderRadius="full" sx={{ "& > div": { background: "var(--brand-primary)" } }} bg="gray.200" />
                                    </Box>
                                </Box>

                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Read</Text>
                                            <Icon as={FiEye} color="#155dfc" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold" color="#155dfc">{messageStats.read.toLocaleString()}</Text>
                                        <Text fontSize="xs" color="gray.600" mt={2}>
                                            {messageStats.totalMessages > 0 ? ((messageStats.read / messageStats.totalMessages) * 100).toFixed(2) : 0}% read rate
                                        </Text>
                                        <Progress size={"sm"} value={messageStats.totalMessages > 0 ? (messageStats.read / messageStats.totalMessages) * 100 : 0} sx={{ "& > div": { background: "var(--brand-primary)" } }} bg="gray.200" mt={2} borderRadius="full" />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Message Status Breakdown */}
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Failed</Text>
                                            <Icon as={FiXCircle} color="#e7000b" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold" color="#e7000b">{messageStats.failed.toLocaleString()}</Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            {messageStats.totalMessages > 0 ? ((messageStats.failed / messageStats.totalMessages) * 100).toFixed(1) : 0}% of total
                                        </Text>
                                    </Box>
                                </Box>

                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Pending</Text>
                                            <Icon as={FiClock} color="#d08700" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold" color="#d08700">{messageStats.pending.toLocaleString()}</Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            {messageStats.totalMessages > 0 ? ((messageStats.pending / messageStats.totalMessages) * 100).toFixed(1) : 0}% of total
                                        </Text>
                                    </Box>
                                </Box>

                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize={"sm"} fontWeight={"500"}>Avg Delivery Time</Text>
                                            <Icon as={FiClock} color="gray.500" />
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold">{messageStats.avgDeliveryTime.toFixed(2)}s</Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>Average time to deliver</Text>
                                    </Box>
                                </Box>
                            </Grid>

                            <ActivityDeviceStats
                                contact={contact}
                                costData={costData}
                                formatDate={formatDate}
                                calculateDuration={calculateDuration}
                                contactId={contactId}
                                metrics={customMetrics}
                                fetchContactStats={fetchContactStats}
                                totalCost={totalCost}
                                hideDevice={true}
                            />

                            {/* Tabs */}
                            <Tabs variant="enclosed" onChange={(index) => setActiveTab(index)}>
                                <TabList bg="#f1f5f9" p="1" borderRadius="full" display="flex" justifyContent="space-between">
                                    <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Overview</Tab>
                                    <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Engagement</Tab>
                                    <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Interactions</Tab>
                                    <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Channel Breakdown</Tab>
                                    <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Recent Messages</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel px={0}>{activeTab === 0 && <OverviewTab id={id} />}</TabPanel>
                                    <TabPanel px={0}>{activeTab === 1 && <TimelineTab contactId={id} />}</TabPanel>
                                    <TabPanel px={0}>{activeTab === 2 && <InteractionsTab contactId={id} />}</TabPanel>
                                    <TabPanel px={0}>{activeTab === 3 && <ChannelBreakdownTab contactId={id} />}</TabPanel>
                                    <TabPanel px={0}>{activeTab === 4 && <RecentMessagesTab contactId={id} phone={contact.number} email={contact.emailAddress} />}</TabPanel>
                                </TabPanels>
                            </Tabs>
                        </VStack>
                    </Grid>

                    {/* Device History Modal */}
                    <Modal isOpen={isDeviceHistoryOpen} onClose={() => setIsDeviceHistoryOpen(false)} size="6xl" scrollBehavior="inside">
                        <ModalOverlay />
                        <ModalContent maxH="85vh">
                            <ModalHeader>
                                <HStack spacing={3}>
                                    <Box bg="blue.100" borderRadius="lg" p={2}>
                                        <Icon as={MdHistory} boxSize={5} color="blue.400" />
                                    </Box>
                                    <Box>
                                        <Text fontSize={"sm"} fontWeight={"500"}>Device History</Text>
                                        <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={0.5}>
                                            {contact.customerName} • {contact.deviceHistory?.length} device{contact.deviceHistory?.length !== 1 ? 's' : ''}
                                        </Text>
                                    </Box>
                                </HStack>
                            </ModalHeader>
                            <ModalBody>
                                <VStack spacing={3} align="stretch">
                                    {contact.deviceHistory?.map((device, index) => (
                                        <Box key={index} borderWidth="1px" borderRadius="lg" overflow="hidden" borderColor={device.status === "active" ? "green.200" : "gray.200"} bg="white">
                                            <Box h="6px" bg={device.status === "active" ? "green.500" : "gray.300"} />
                                            <Box p={4}>
                                                <Grid templateColumns="130px 200px 1fr 280px" gap={4}>
                                                    <Box>
                                                        {device.status === "active" ? (
                                                            <HStack bg="green.50" color="green.700" px={3} py={2} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                                                <Icon as={FiCheckCircle} />
                                                                <Text fontWeight="medium" fontSize="sm">Active</Text>
                                                            </HStack>
                                                        ) : (
                                                            <HStack bg="gray.50" color="gray.600" px={3} py={2} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                                                <Icon as={FiClock} />
                                                                <Text fontWeight="medium" fontSize="sm">Inactive</Text>
                                                            </HStack>
                                                        )}
                                                    </Box>
                                                    <HStack spacing={3} align="center">
                                                        <Box flexShrink={0} boxSize={8} display="flex" alignItems="center" justifyContent="center" borderRadius="lg" bg={device.status === "active" ? "green.50" : "gray.50"} borderWidth="1px" borderColor={device.status === "active" ? "green.100" : "gray.200"}>
                                                            <Icon as={FiSmartphone} boxSize={6} color={device.status === "active" ? "green.600" : "gray.400"} />
                                                        </Box>
                                                        <VStack align="start" spacing={0.5}>
                                                            {device.deviceType && (
                                                                <Text fontWeight="semibold">{device.deviceType}</Text>
                                                            )}
                                                            {device.deviceOS && (
                                                                <Text fontSize="sm" color="gray.600">{device.deviceOS}</Text>
                                                            )}
                                                            {device.deviceModel && (
                                                                <Text fontSize="xs" color="gray.500">{device.deviceModel}</Text>
                                                            )}
                                                            {device.timezone && (
                                                                <Text fontSize="xs" color="gray.400">TZ: {device.timezone}</Text>
                                                            )}
                                                        </VStack>
                                                    </HStack>
                                                    <Box overflow="hidden">
                                                        <HStack spacing={1} mb={1}>
                                                            <Icon as={FiHash} boxSize={3} color="gray.500" />
                                                            <Text fontSize="xs" color="gray.500">Subscriber ID</Text>
                                                        </HStack>
                                                        <Text fontSize="sm" fontWeight="semibold" fontFamily="mono" dir="ltr" textAlign="left" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                                                            {device.subscriberId}
                                                        </Text>
                                                    </Box>
                                                    <HStack spacing={3} align="center">
                                                        <Box flexShrink={0}>
                                                            <Text fontSize="xs" color="gray.500" mb={1} whiteSpace="nowrap">Activated</Text>
                                                            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">{device.activatedDate ? formatDate(device.activatedDate) : "N/A"}</Text>
                                                        </Box>
                                                        <Text color="gray.300" flexShrink={0}>→</Text>
                                                        <Box flexShrink={0}>
                                                            {device.deactivatedDate ? (
                                                                <>
                                                                    <Text fontSize="xs" color="gray.500" mb={1} whiteSpace="nowrap">Deactivated</Text>
                                                                    <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">{formatDate(device.deactivatedDate)}</Text>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Text fontSize="xs" color="gray.400" mb={1} whiteSpace="nowrap">Deactivated</Text>
                                                                    <Text fontSize="sm" color="gray.400">—</Text>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </HStack>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    ))}
                                </VStack>
                            </ModalBody>
                            <ModalFooter bg="gray.50" borderTopWidth="1px">
                                <Flex w="full" justify="space-between" align="center">
                                    <Text fontSize="sm" color="gray.600">
                                        Total devices: <Text as="span" fontWeight="bold" color="gray.900">{contact.deviceHistory?.length}</Text>
                                    </Text>
                                    <Button variant="outline" onClick={() => setIsDeviceHistoryOpen(false)}>Close</Button>
                                </Flex>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </VStack>
            </BoxWrapper>
        </>
    );
}